/**
 * PushNotificationService - Enhanced Push Notification Handler
 *
 * Extends notification handling with deep linking navigation, local notification
 * display for foreground, and integration with Angular Router.
 *
 * CRITICAL CONSTRAINTS:
 * - Use devLog() instead of console.log
 * - No PII in notification payloads
 * - All types must be explicit (zero any usage)
 *
 * @see WI-035 - Push Notification Handler
 * @see notification.service.ts for base FCM functionality (WI-033)
 */

import { Injectable, inject, signal, computed, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Capacitor } from '@capacitor/core';
import { LocalNotifications } from '@capacitor/local-notifications';
import { ActionPerformed } from '@capacitor/push-notifications';
import { devLog, devError } from '@core';
import { NotificationService } from './notification.service';
import {
  PushNotificationState,
  NotificationType,
  DeepLinkConfig
} from '@core/types/push-notification.types';
import { Subscription, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

/**
 * Enhanced Push Notification Service with deep linking and local notifications
 */
@Injectable({ providedIn: 'root' })
export class PushNotificationService implements OnDestroy {
  private readonly router = inject(Router);
  private readonly notificationService = inject(NotificationService);

  // Subscription management
  private readonly subscriptions = new Subscription();

  // Reactive state using signals
  private readonly _state = signal<PushNotificationState>({
    isRegistered: false,
    hasPermission: false,
    fcmToken: null,
    lastError: null
  });

  /** Current notification state (read-only) */
  readonly state = this._state.asReadonly();

  /** Whether push is available on this platform */
  readonly isAvailable = computed((): boolean =>
    Capacitor.isNativePlatform() &&
    (Capacitor.getPlatform() === 'ios' || Capacitor.getPlatform() === 'android')
  );

  constructor() {
    devLog('[PushNotificationService] Constructed');
  }

  /**
   * Initialize push notification listeners and handlers
   * Call this once during app initialization
   */
  async initialize(): Promise<void> {
    if (!this.isAvailable()) {
      devLog('[PushNotificationService] Push not available on this platform');
      return;
    }

    // Request local notification permissions (for foreground display)
    await this.requestLocalNotificationPermission();

    // Subscribe to notification events
    this.setupNotificationHandlers();

    devLog('[PushNotificationService] Initialized');
  }

  /**
   * Register device for push notifications
   * Wraps NotificationService.initializeForUser with state management
   */
  async registerForPush(): Promise<boolean> {
    if (!this.isAvailable()) {
      this.updateState({ lastError: 'Push not available on this platform' });
      return false;
    }

    try {
      // Check permission first
      const permission = await this.notificationService.checkPermissions();

      if (permission !== 'granted') {
        const requested = await this.notificationService.requestPermissions();
        if (requested !== 'granted') {
          this.updateState({
            hasPermission: false,
            lastError: 'Permission denied'
          });
          devLog('[PushNotificationService] Permission denied');
          return false;
        }
      }

      this.updateState({ hasPermission: true });

      // Register and save token
      await this.notificationService.initializeForUser();

      // Get token
      const token = await this.notificationService.getToken();

      this.updateState({
        isRegistered: !!token,
        fcmToken: token,
        lastError: null
      });

      devLog('[PushNotificationService] Registration successful');
      return true;

    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Registration failed';
      this.updateState({ lastError: message });
      devError('[PushNotificationService] Registration error:', message);
      return false;
    }
  }

  /**
   * Unregister device from push notifications
   */
  async unregister(): Promise<void> {
    if (!this.isAvailable()) return;

    try {
      await this.notificationService.unregister();

      this.updateState({
        isRegistered: false,
        fcmToken: null
      });

      devLog('[PushNotificationService] Unregistered');

    } catch (error: unknown) {
      devError('[PushNotificationService] Unregister error:', error);
    }
  }

  /**
   * Listen for notification action performed events
   * Exposes the underlying notification action observable for external handlers (WI-037)
   * Maps internal NotificationActionEvent to Capacitor's ActionPerformed format
   */
  onActionPerformed(): Observable<ActionPerformed> {
    return this.notificationService.onNotificationAction().pipe(
      map((event): ActionPerformed => {
        const notificationId = String(Date.now()); // Generate a unique ID
        return {
          actionId: event.actionId || 'default',
          inputValue: '',
          notification: {
            id: notificationId,
            title: event.notification.payload.title,
            body: event.notification.payload.body,
            data: event.notification.payload.data || {}
          }
        };
      })
    );
  }

  /**
   * Request local notification permission for foreground display
   */
  private async requestLocalNotificationPermission(): Promise<boolean> {
    try {
      const result = await LocalNotifications.requestPermissions();
      const granted = result.display === 'granted';
      devLog('[PushNotificationService] Local notification permission:', granted ? 'granted' : 'denied');
      return granted;
    } catch (error: unknown) {
      devError('[PushNotificationService] Local notification permission error:', error);
      return false;
    }
  }

  /**
   * Setup handlers for notification events
   */
  private setupNotificationHandlers(): void {
    // Handle foreground notifications - display as local notification
    const receivedSub = this.notificationService.onNotificationReceived()
      .subscribe((notification): void => {
        if (notification.foreground) {
          this.displayLocalNotification(
            notification.payload.title,
            notification.payload.body,
            notification.payload.data
          );
        }
      });

    // Handle notification tap - navigate to deep link
    const actionSub = this.notificationService.onNotificationAction()
      .subscribe((event): void => {
        const data = event.notification.payload.data;
        this.handleNotificationTap(data);
      });

    // Handle token refresh - update state
    const tokenSub = this.notificationService.onTokenRefresh()
      .subscribe((token): void => {
        this.updateState({ fcmToken: token });
      });

    this.subscriptions.add(receivedSub);
    this.subscriptions.add(actionSub);
    this.subscriptions.add(tokenSub);
  }

  /**
   * Display local notification for foreground push
   */
  private async displayLocalNotification(
    title: string,
    body: string,
    data?: Record<string, string>
  ): Promise<void> {
    try {
      await LocalNotifications.schedule({
        notifications: [{
          id: Date.now(),
          title: title || 'CheckerFly',
          body: body || '',
          extra: data,
          sound: 'default'
        }]
      });
      devLog('[PushNotificationService] Local notification scheduled');
    } catch (error: unknown) {
      devError('[PushNotificationService] Failed to schedule local notification:', error);
    }
  }

  /**
   * Handle notification tap and navigate to appropriate screen
   */
  private async handleNotificationTap(data?: Record<string, string>): Promise<void> {
    devLog('[PushNotificationService] Notification tapped:', data);

    // Priority: deepLink > checkInId > default
    if (data?.['deepLink']) {
      await this.navigateToDeepLink(data['deepLink']);
    } else if (data?.['checkInId']) {
      await this.router.navigate(['/check-ins', data['checkInId']]);
    } else {
      await this.router.navigate(['/check-ins']);
    }
  }

  /**
   * Navigate to deep link URL
   */
  private async navigateToDeepLink(deepLink: string): Promise<void> {
    try {
      // Parse deep link - could be full URL or relative path
      if (deepLink.startsWith('http')) {
        const url = new URL(deepLink);
        await this.router.navigateByUrl(url.pathname + url.search);
      } else {
        await this.router.navigateByUrl(deepLink);
      }
    } catch (error: unknown) {
      devError('[PushNotificationService] Deep link navigation failed:', error);
      // Fall back to check-ins list
      await this.router.navigate(['/check-ins']);
    }
  }

  /**
   * Build deep link URL for notification navigation
   * @param config - Deep link configuration
   * @returns Formatted deep link string
   */
  buildDeepLink(config: DeepLinkConfig): string {
    let link = config.route;

    if (config.params) {
      const queryString = new URLSearchParams(config.params).toString();
      if (queryString) {
        link += `?${queryString}`;
      }
    }

    if (config.fragment) {
      link += `#${config.fragment}`;
    }

    return link;
  }

  /**
   * Get human-readable label for notification type
   * @param type - The notification type
   * @returns Localized label string for the notification type
   */
  getNotificationTypeLabel(type: NotificationType): string {
    const labels: Record<NotificationType, string> = {
      nudge: 'Check-in Reminder',
      nudge_final: 'Final Reminder',
      escalation: 'Contact Alert',
      resolved: 'Check-in Resolved',
      test: 'Test Notification'
    };
    return labels[type] || 'Notification';
  }

  /**
   * Update internal state
   */
  private updateState(updates: Partial<PushNotificationState>): void {
    this._state.update(state => ({ ...state, ...updates }));
  }

  /**
   * Cleanup on destroy
   */
  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    devLog('[PushNotificationService] Destroyed');
  }
}
